/**
 * Client error reporting (Sentry), switched on only by REACT_APP_SENTRY_DSN.
 *
 * Without a DSN this module does nothing and the Sentry SDK is never
 * downloaded: ./sentryClient (the only file importing @sentry/react) is
 * pulled in with a dynamic import() the first time it is needed.
 */
import type { ReportContext } from './sentryClient';
import { isNativeApp } from './platform';

type SentryClientModule = typeof import('./sentryClient');

let clientPromise: Promise<SentryClientModule | null> | null = null;

export const getSentryDsn = (): string => (process.env.REACT_APP_SENTRY_DSN || '').trim();

export const isErrorReportingEnabled = (): boolean => Boolean(getSentryDsn());

const detectPlatform = (): 'web' | 'native' => {
  try {
    return isNativeApp() ? 'native' : 'web';
  } catch {
    return 'web';
  }
};

/**
 * Load and start Sentry once. Resolves to null when there is no DSN or the
 * SDK fails to load (error reporting must never break the app).
 */
export const initErrorReporting = (): Promise<SentryClientModule | null> => {
  const dsn = getSentryDsn();
  if (!dsn) {
    return Promise.resolve(null);
  }
  if (!clientPromise) {
    clientPromise = import('./sentryClient')
      .then((client) => {
        client.startSentry({
          dsn,
          environment: process.env.REACT_APP_ENV || process.env.NODE_ENV || 'production',
          platform: detectPlatform(),
        });
        return client;
      })
      .catch(() => null);
  }
  return clientPromise;
};

/** Report an error (e.g. from an error boundary). No-op without a DSN. */
export const reportError = (error: unknown, context?: ReportContext): void => {
  if (!isErrorReportingEnabled()) {
    return;
  }
  void initErrorReporting().then((client) => {
    try {
      client?.reportToSentry(error, context);
    } catch {
      // Never let reporting throw.
    }
  });
};

/** Test-only: forget the loaded client. */
export const __resetErrorReportingForTests = (): void => {
  clientPromise = null;
};
